import { useBackupExport, useBackupImport, useBackupStatus } from '../api/intelligence';
import { useRef } from 'react';

export default function BackupPage() {
  const { data: status, isLoading } = useBackupStatus();
  const exportMutation = useBackupExport();
  const importMutation = useBackupImport();
  const fileRef = useRef<HTMLInputElement>(null);

  const handleExport = async () => {
    const blob = await exportMutation.mutateAsync();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `helios-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = () => {
    const file = fileRef.current?.files?.[0];
    if (file) importMutation.mutate(file);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-gray-100">Backup & Restore</h1>
        <p className="text-sm text-gray-500 mt-1">Sauvegarde et restauration de la base de donnees</p>
      </div>

      {/* Stats */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-2 border-cyber-cyan/30 border-t-cyber-cyan rounded-full animate-spin" />
        </div>
      )}

      {status && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="glass-panel p-4 text-center">
            <p className="text-2xl font-bold text-cyber-cyan">{status.entity_count}</p>
            <p className="text-xs text-gray-500 mt-1">Entites</p>
          </div>
          <div className="glass-panel p-4 text-center">
            <p className="text-2xl font-bold text-cyber-cyan">{status.relationship_count}</p>
            <p className="text-xs text-gray-500 mt-1">Relations</p>
          </div>
          <div className="glass-panel p-4 text-center">
            <p className="text-2xl font-bold text-cyber-cyan">
              {Object.keys(status.table_sizes || {}).length}
            </p>
            <p className="text-xs text-gray-500 mt-1">Tables</p>
          </div>
        </div>
      )}

      {/* Export */}
      <div className="glass-panel p-5 space-y-3">
        <h3 className="text-sm font-semibold text-gray-300">Exporter</h3>
        <p className="text-xs text-gray-500">
          Telecharger une sauvegarde JSON complete de toutes les entites, relations et annotations.
        </p>
        <button
          onClick={handleExport}
          disabled={exportMutation.isPending}
          className="px-5 py-2 text-sm font-medium bg-cyber-cyan/10 text-cyber-cyan rounded-lg hover:bg-cyber-cyan/20 disabled:opacity-50 transition-colors"
        >
          {exportMutation.isPending ? 'Export en cours...' : 'Telecharger la sauvegarde'}
        </button>
      </div>

      {/* Import */}
      <div className="glass-panel p-5 space-y-3">
        <h3 className="text-sm font-semibold text-gray-300">Restaurer</h3>
        <p className="text-xs text-gray-500">
          Importer une sauvegarde JSON. Les entites existantes (meme ID) seront ignorees.
        </p>
        <div className="flex items-center gap-4">
          <input
            ref={fileRef}
            type="file"
            accept=".json"
            className="text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-surface-tertiary file:text-gray-300 hover:file:bg-surface-elevated file:cursor-pointer file:transition-colors"
          />
          <button
            onClick={handleImport}
            disabled={importMutation.isPending}
            className="px-5 py-2 text-sm font-medium bg-amber-500/10 text-amber-400 rounded-lg hover:bg-amber-500/20 disabled:opacity-50 transition-colors"
          >
            {importMutation.isPending ? 'Restauration...' : 'Restaurer'}
          </button>
        </div>

        {importMutation.data && (
          <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-xs text-green-400">
            Restauration terminee : {importMutation.data.entities_imported || 0} entites,{' '}
            {importMutation.data.relationships_imported || 0} relations
          </div>
        )}
      </div>
    </div>
  );
}
